/**
 * Super Admin — Arena pages.
 *  /superadmin/arena/applications — pending gym registrations (approve/reject)
 *  /superadmin/arena/gyms — active gyms list
 *  /superadmin/arena/passes — Arena Pass MRR snapshot
 *  /superadmin/arena/checkins — check-ins log
 *  /superadmin/arena/disputes — disputes queue (stub)
 *  /superadmin/arena/revenue — revenue + pending payouts
 */
import { useEffect, useState, useCallback } from "react";
import { Check, X } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import {
  Card, TableShell, Th, Td, Modal, PrimaryBtn, GhostBtn, DangerBtn, EmptyState,
  fmtINR, relTime, FilterPills, inputStyle, fieldLabel, SummaryTiles,
} from "./_atoms";

// ───────────────────────────────────────────────────────────────────
export function ArenaApplications() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    apiClient.get("/superadmin/arena/applications", { params: { status } })
      .then((r) => setRows(r.data?.applications || []));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await apiClient.post(`/superadmin/arena/applications/${id}/approve`);
      toast.success("Approved");
      setOpenId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (id, reason) => {
    if (!reason || reason.length < 10) return toast.error("Reason ≥10 chars required");
    try {
      await apiClient.post(`/superadmin/arena/applications/${id}/reject`, { reason });
      toast.success("Rejected");
      setOpenId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="arena-applications">
      <SectionHeader title="Arena Applications" onRefresh={load} />
      <Card>
        <FilterPills value={status} onChange={setStatus}
          options={[
            { value: "pending", label: "Pending" },
            { value: "active", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]} testid="app-status" />
      </Card>
      {rows.length === 0 ? <EmptyState title="No applications" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="apps-table">
            <thead><tr><Th>Gym</Th><Th>Owner</Th><Th>City</Th><Th>Plan</Th><Th>Code</Th><Th>Submitted</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.gym_id}>
                  <Td><strong>{g.gym_name}</strong><div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>{g.gym_type}</div></Td>
                  <Td style={{ fontSize: 12 }}>{g.owner_name}<div style={{ fontSize: 11 }}>{g.owner_phone}</div></Td>
                  <Td>{g.city}</Td>
                  <Td>{g.plan_tier}</Td>
                  <Td><code style={{ fontSize: 11 }}>{g.gym_code}</code></Td>
                  <Td>{relTime(g.created_at)}</Td>
                  <Td>
                    <GhostBtn onClick={() => setOpenId(g)} style={{ height: 30, padding: "0 12px", fontSize: 11 }} data-testid={`view-app-${g.gym_id}`}>Review</GhostBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {openId && <ReviewModal app={openId} onClose={() => setOpenId(null)} onApprove={approve} onReject={reject} />}
    </div>
  );
}

function ReviewModal({ app, onClose, onApprove, onReject }) {
  const [reason, setReason] = useState("");
  return (
    <Modal open onClose={onClose} title={app.gym_name} maxWidth={620}>
      <div style={{ display: "grid", gap: 10 }}>
        <Card>
          <Row label="Type" value={app.gym_type} />
          <Row label="City" value={app.city} />
          <Row label="Address" value={app.address} />
          <Row label="Owner" value={`${app.owner_name} · ${app.owner_phone}`} />
          <Row label="Email" value={app.owner_email || "—"} />
          <Row label="Members / Trainers" value={`${app.member_count_approx} / ${app.trainer_count}`} />
          <Row label="Fee range" value={`₹${app.membership_fee_min} – ₹${app.membership_fee_max}`} />
          <Row label="Amenities" value={(app.amenities || []).join(", ") || "—"} />
          <Row label="Plan" value={<strong>{app.plan_tier}</strong>} />
          <Row label="Gym code" value={<code>{app.gym_code}</code>} />
        </Card>
        {app.photos?.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))", gap: 6 }}>
            {app.photos.map((src, i) => <img key={i} src={src} alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", borderRadius: 8 }} />)}
          </div>
        )}
        {app.status === "pending" && (
          <>
            <label style={fieldLabel}>Reject reason (≥10 chars)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...inputStyle, height: 70, padding: "10px 14px" }} data-testid="reject-reason" />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <DangerBtn onClick={() => onReject(app.gym_id, reason)} data-testid="reject-app"><X size={14} /> Reject</DangerBtn>
              <PrimaryBtn onClick={() => onApprove(app.gym_id)} data-testid="approve-app"><Check size={14} /> Approve</PrimaryBtn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13, gap: 12 }}>
      <span style={{ color: "var(--text-tertiary, #8A8884)" }}>{label}</span>
      <span style={{ color: "var(--text-primary, #0C0C0B)", maxWidth: "60%", textAlign: "right" }}>{value}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function ArenaGyms() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    apiClient.get("/superadmin/arena/gyms").then((r) => setRows(r.data?.gyms || []));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="arena-gyms">
      <SectionHeader title="Active Arena Gyms" onRefresh={load} />
      {rows.length === 0 ? <EmptyState title="No active gyms" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Gym</Th><Th>City</Th><Th>Plan</Th><Th>Members*</Th><Th>Check-ins</Th><Th>Code</Th></tr></thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.gym_id}>
                  <Td><strong>{g.gym_name}</strong></Td>
                  <Td>{g.city}</Td>
                  <Td>{g.plan_tier}</Td>
                  <Td>{g.member_count_approx}</Td>
                  <Td>{g.total_checkins || 0}</Td>
                  <Td><code style={{ fontSize: 11 }}>{g.gym_code}</code></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      <p style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>* Approximate count from gym onboarding form. Actual linked members shown in gym dashboard.</p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function ArenaPasses() {
  const [data, setData] = useState({ by_plan: [], total_active: 0 });
  const load = useCallback(() => {
    apiClient.get("/superadmin/arena/passes").then((r) => setData(r.data || {})).catch(() => setData({ by_plan: [], total_active: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const totalMRR = (data.by_plan || []).reduce((s, p) => s + (p.mrr || 0), 0);
  return (
    <div className="space-y-4" data-testid="arena-passes">
      <SectionHeader title="Arena Pass" onRefresh={load} />
      <SummaryTiles tiles={[
        { label: "Active Passes", value: data.total_active || 0 },
        { label: "Active MRR", value: fmtINR(totalMRR) },
        { label: "Plans live", value: (data.by_plan || []).length },
      ]} />
      {(data.by_plan || []).length > 0 && (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Plan</Th><Th>Subscribers</Th><Th>Monthly Revenue</Th></tr></thead>
            <tbody>
              {data.by_plan.map((p) => (
                <tr key={p._id || "unknown"}>
                  <Td><strong>{(p._id || "unknown").toUpperCase()}</strong></Td>
                  <Td>{p.count}</Td>
                  <Td>{fmtINR(p.mrr)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
        Arena Pass billing UI lands in Session 2. Right now this dashboard reflects whatever subscriptions exist in the DB.
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function ArenaCheckinsAdmin() {
  const [data, setData] = useState({ checkins: [], total: 0 });
  const load = useCallback(() => {
    apiClient.get("/superadmin/arena/checkins", { params: { limit: 200 } }).then((r) => setData(r.data || {}));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="arena-checkins-admin">
      <SectionHeader title="Check-ins" onRefresh={load} />
      <SummaryTiles tiles={[{ label: "Total check-ins", value: data.total || 0 }]} />
      {data.checkins.length === 0 ? <EmptyState title="No check-ins yet" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>When</Th><Th>Member</Th><Th>Gym</Th><Th>Pass?</Th><Th>Duration</Th></tr></thead>
            <tbody>
              {data.checkins.map((c) => (
                <tr key={c.checkin_id}>
                  <Td>{relTime(c.checked_in_at)}</Td>
                  <Td>{c.user_name || c.user_id?.slice(0, 12)}</Td>
                  <Td>{c.gym_name || c.gym_id?.slice(0, 12)}</Td>
                  <Td>{c.arena_pass_credit_used ? "Yes" : "—"}</Td>
                  <Td>{c.duration_minutes ? `${c.duration_minutes}m` : "active"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function ArenaDisputes() {
  return (
    <div className="space-y-4" data-testid="arena-disputes">
      <SectionHeader title="Disputes" />
      <EmptyState title="No open disputes" sub="Missing-checkin reports will appear here as members file them." />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function ArenaRevenue() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/arena/revenue").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div data-testid="arena-revenue"><SectionHeader title="Arena Revenue" onRefresh={load} /><EmptyState title="Loading…" /></div>;
  const totalPending = (data.pending_payouts || []).reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="arena-revenue">
      <SectionHeader title="Arena Revenue" onRefresh={load} />
      <SummaryTiles tiles={[
        { label: "Active Pass MRR", value: fmtINR(data.active_mrr) },
        { label: "Gyms paid (30d)", value: (data.by_gym_30d || []).length },
        { label: "Pending Payouts", value: fmtINR(totalPending) },
      ]} />
      {(data.by_gym_30d || []).length > 0 ? (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Gym</Th><Th>Visits (30d)</Th><Th>Amount</Th></tr></thead>
            <tbody>
              {data.by_gym_30d.map((g, i) => (
                <tr key={i}>
                  <Td><code>{g._id?.slice(0, 14)}</code></Td>
                  <Td>{g.visits}</Td>
                  <Td>{fmtINR(g.amount)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      ) : (
        <EmptyState title="No paid visits yet" sub="Arena Pass visits trigger gym payouts. Until passes go live, nothing is pending." />
      )}
    </div>
  );
}
